import React, { forwardRef } from "react";

// Brand tokens (explicit values so html2canvas renders reliably regardless of CSS vars).
const C = {
  bg: "#0B0B0C",
  panel: "#141416",
  border: "#2A2A2E",
  gold: "#C8A14A",
  goldSoft: "#DFC878",
  white: "#F5F5F4",
  muted: "#9C9C9C",
  secondary: "#C9C9C7",
};

const LEVEL_TIERS = {
  essential: ["essential"],
  deep: ["essential", "deep"],
  ascendia: ["essential", "deep", "ascendia"],
};

/**
 * Print-perfect, on-brand property plan document. Rendered off-screen and
 * captured to PDF via downloadPlanPdf(). Uses inline styles only.
 */
export const PlanPdfDocument = forwardRef(function PlanPdfDocument(
  { est, dur, cx, addonLabels = [], needLabel, levelLabel, levelKey = "essential", s = {}, areas = [], aiSummary = "", site = {} },
  ref
) {
  const tiers = LEVEL_TIERS[levelKey] || ["essential"];
  const range = est ? `${est.currency}${(est.low || 0).toLocaleString()} – ${est.currency}${(est.high || 0).toLocaleString()}` : "On review";
  const durBand = dur?.band && dur.band !== "—" ? dur.band : (dur?.low ? `${dur.low}–${dur.high} hrs` : "To be confirmed");
  const phone = site?.phone_intl || "972587790553";

  const label = (t) => (
    <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: C.goldSoft, marginBottom: 6 }}>{t}</div>
  );

  return (
    <div ref={ref} style={{ position: "fixed", left: -99999, top: 0, width: 794, background: C.bg, color: C.white, fontFamily: "Georgia, 'Times New Roman', serif" }}>
      <div style={{ padding: "40px 44px" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: `1px solid ${C.border}`, paddingBottom: 18 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: 1 }}>ASCENDIA <span style={{ color: C.gold }}>DETAIL</span></div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2, letterSpacing: 3, textTransform: "uppercase" }}>Property Intelligence Plan</div>
          </div>
          <div style={{ textAlign: "right", fontSize: 11, color: C.muted }}>
            <div>{new Date().toLocaleDateString()}</div>
            <div style={{ marginTop: 2 }}>ascendiadetail.com</div>
          </div>
        </div>

        {/* Estimate hero */}
        <div style={{ marginTop: 22, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, padding: 22 }}>
          {label("Estimated investment (guide)")}
          <div style={{ fontSize: 34, fontWeight: 700, color: C.white }}>{range}</div>
          <div style={{ fontSize: 12, color: C.secondary, marginTop: 8 }}>
            {needLabel} · {levelLabel} level · Est. on-site time {durBand} · Complexity {cx?.level || "—"}
          </div>
        </div>

        {/* Property facts */}
        <div style={{ marginTop: 20 }}>
          {label("Your property")}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {[
              s.propertyType, s.city, s.sqm ? `${s.sqm} m²` : null,
              s.bathrooms ? `${s.bathrooms} bath` : null, s.furnishing,
              s.conditionKey ? `Condition: ${s.conditionKey}` : null,
            ].filter(Boolean).map((t, i) => (
              <span key={i} style={{ border: `1px solid ${C.border}`, borderRadius: 20, padding: "4px 12px", fontSize: 11, color: C.secondary }}>{t}</span>
            ))}
          </div>
        </div>

        {/* AI summary */}
        {aiSummary ? (
          <div style={{ marginTop: 20, background: "rgba(200,161,74,0.06)", border: `1px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
            {label("Ascendia Intelligence · AI assessment")}
            <div style={{ fontSize: 12.5, color: C.secondary, lineHeight: 1.6 }}>{aiSummary}</div>
          </div>
        ) : null}

        {/* Room breakdown */}
        <div style={{ marginTop: 22 }}>
          {label("Room-by-room scope")}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {areas.map((a) => {
              const items = tiers.flatMap((t) => a.scope?.[t] || []);
              return (
                <div key={a.id} style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.white, marginBottom: 8 }}>{a.name}</div>
                  {items.map((it, i) => (
                    <div key={i} style={{ fontSize: 11.5, color: C.secondary, marginBottom: 4, paddingLeft: 12, position: "relative" }}>
                      <span style={{ position: "absolute", left: 0, color: C.gold }}>·</span>{it}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        {/* Add-ons */}
        {addonLabels.length > 0 && (
          <div style={{ marginTop: 20 }}>
            {label("Included detailing add-ons")}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {addonLabels.map((l, i) => (
                <span key={i} style={{ border: `1px solid ${C.gold}`, borderRadius: 20, padding: "4px 12px", fontSize: 11, color: C.goldSoft }}>{l}</span>
              ))}
            </div>
          </div>
        )}

        {/* Timeline */}
        <div style={{ marginTop: 20 }}>
          {label("Project timeline")}
          <div style={{ display: "flex", gap: 10 }}>
            {[["Preparation & access", "Before the day"], ["On-site detailing", durBand], ["Final inspection & handover", "Same visit"]].map(([t, tag], i) => (
              <div key={i} style={{ flex: 1, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14 }}>
                <div style={{ fontSize: 10, color: C.goldSoft, letterSpacing: 1 }}>PHASE {i + 1}</div>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: C.white, marginTop: 4 }}>{t}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{tag}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={{ marginTop: 26, borderTop: `1px solid ${C.border}`, paddingTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 10.5, color: C.muted, maxWidth: 460, lineHeight: 1.5 }}>
            All pricing and timing are estimates from Ascendia's pricing engine, pending a final review of your property. This plan is not a binding quote.
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 12, color: C.gold, fontWeight: 700 }}>Ready to book?</div>
            <div style={{ fontSize: 11, color: C.secondary, marginTop: 2 }}>WhatsApp +{phone}</div>
          </div>
        </div>
      </div>
    </div>
  );
});

/**
 * Capture a DOM node to a multi-page A4 PDF and trigger download.
 * Lazy-loads jspdf + html2canvas so they don't bloat the main bundle.
 */
export async function downloadPlanPdf(node, filename = "Ascendia-Property-Plan.pdf") {
  if (!node) return;
  const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
    import("jspdf"),
    import("html2canvas"),
  ]);
  const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#0B0B0C", useCORS: true, logging: false });
  const img = canvas.toDataURL("image/jpeg", 0.92);
  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const imgH = (canvas.height * pageW) / canvas.width;
  let heightLeft = imgH;
  let position = 0;
  pdf.addImage(img, "JPEG", 0, position, pageW, imgH);
  heightLeft -= pageH;
  while (heightLeft > 0) {
    position -= pageH;
    pdf.addPage();
    pdf.addImage(img, "JPEG", 0, position, pageW, imgH);
    heightLeft -= pageH;
  }
  pdf.save(filename);
}
